import React from 'react';
import { TrendingUp, FileClock, Briefcase, ArrowDownUp, Package } from 'lucide-react';

/**
 * Компонент дополнительных финансовых показателей для калькулятора рентабельности
 */
const AdditionalMetricsSection = ({ 
  formData, 
  handleInputChange, 
  errors 
}) => {
  // Если опция дополнительных метрик отключена, не отображаем компонент
  if (!formData.includeAdditionalMetrics) return null;

  return (
    <div className="mb-6 bg-purple-50 dark:bg-purple-900/20 p-4 rounded-lg border border-purple-200 dark:border-purple-800">
      <h3 className="text-base font-medium text-gray-900 dark:text-white mb-4 flex items-center">
        <TrendingUp className="w-5 h-5 mr-2 text-purple-500" />
        Расширенные финансовые показатели
      </h3>
      
      <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
        {/* Процентные расходы */}
        <div>
          <label htmlFor="interestExpense" className="block text-sm font-medium text-gray-700 dark:text-gray-300 mb-1 flex items-center">
            <TrendingUp className="w-4 h-4 mr-2 text-purple-500" />
            Процентные расходы (руб.)
          </label>
          <input
            type="text"
            id="interestExpense"
            name="interestExpense"
            value={formData.interestExpense}
            onChange={handleInputChange}
            className={`form-input w-full ${errors.interestExpense ? 'input-error' : ''}`}
            placeholder="Расходы на обслуживание кредитов"
          />
          {errors.interestExpense && (
            <p className="mt-1 text-xs text-red-500 dark:text-red-400">
              {errors.interestExpense}
            </p>
          )}
          <p className="text-xs text-gray-500 dark:text-gray-400 mt-1">
            Проценты по кредитам, займам и прочим долговым обязательствам
          </p>
        </div>

        {/* Амортизация */}
        <div>
          <label htmlFor="depreciation" className="block text-sm font-medium text-gray-700 dark:text-gray-300 mb-1 flex items-center">
            <FileClock className="w-4 h-4 mr-2 text-purple-500" />
            Амортизация (руб.)
          </label>
          <input
            type="text"
            id="depreciation"
            name="depreciation"
            value={formData.depreciation}
            onChange={handleInputChange}
            className={`form-input w-full ${errors.depreciation ? 'input-error' : ''}`}
            placeholder="Сумма амортизации за период"
          />
          {errors.depreciation && (
            <p className="mt-1 text-xs text-red-500 dark:text-red-400">
              {errors.depreciation}
            </p>
          )}
          <p className="text-xs text-gray-500 dark:text-gray-400 mt-1">
            Амортизационные отчисления за рассматриваемый период
          </p>
        </div>

        {/* Дебиторская задолженность */}
        <div>
          <label htmlFor="accountsReceivable" className="block text-sm font-medium text-gray-700 dark:text-gray-300 mb-1 flex items-center">
            <ArrowDownUp className="w-4 h-4 mr-2 text-purple-500" />
            Дебиторская задолженность (руб.)
          </label>
          <input
            type="text"
            id="accountsReceivable"
            name="accountsReceivable"
            value={formData.accountsReceivable}
            onChange={handleInputChange}
            className={`form-input w-full ${errors.accountsReceivable ? 'input-error' : ''}`}
            placeholder="Сумма дебиторской задолженности"
          />
          {errors.accountsReceivable && (
            <p className="mt-1 text-xs text-red-500 dark:text-red-400">
              {errors.accountsReceivable}
            </p>
          )}
          <p className="text-xs text-gray-500 dark:text-gray-400 mt-1">
            Средства, которые должны вашей компании клиенты и партнеры
          </p>
        </div>

        {/* Кредиторская задолженность */}
        <div>
          <label htmlFor="accountsPayable" className="block text-sm font-medium text-gray-700 dark:text-gray-300 mb-1 flex items-center">
            <Briefcase className="w-4 h-4 mr-2 text-purple-500" />
            Кредиторская задолженность (руб.)
          </label>
          <input
            type="text"
            id="accountsPayable"
            name="accountsPayable"
            value={formData.accountsPayable}
            onChange={handleInputChange}
            className={`form-input w-full ${errors.accountsPayable ? 'input-error' : ''}`}
            placeholder="Сумма кредиторской задолженности"
          />
          {errors.accountsPayable && (
            <p className="mt-1 text-xs text-red-500 dark:text-red-400">
              {errors.accountsPayable}
            </p>
          )}
          <p className="text-xs text-gray-500 dark:text-gray-400 mt-1">
            Средства, которые ваша компания должна поставщикам и подрядчикам
          </p>
        </div>

        {/* Запасы */}
        <div>
          <label htmlFor="inventory" className="block text-sm font-medium text-gray-700 dark:text-gray-300 mb-1 flex items-center">
            <Package className="w-4 h-4 mr-2 text-purple-500" />
            Запасы (руб.)
          </label>
          <input
            type="text"
            id="inventory"
            name="inventory"
            value={formData.inventory}
            onChange={handleInputChange}
            className={`form-input w-full ${errors.inventory ? 'input-error' : ''}`}
            placeholder="Стоимость запасов товаров/материалов"
          />
          {errors.inventory && (
            <p className="mt-1 text-xs text-red-500 dark:text-red-400">
              {errors.inventory}
            </p>
          )}
          <p className="text-xs text-gray-500 dark:text-gray-400 mt-1">
            Общая стоимость запасов, сырья, материалов и готовой продукции
          </p>
        </div>
      </div>

      <div className="mt-4 p-3 bg-purple-100 dark:bg-purple-900/30 border border-purple-200 dark:border-purple-800 rounded-lg">
        <p className="text-xs text-purple-700 dark:text-purple-300">
          <strong>Зачем нужны эти показатели?</strong> Дополнительные финансовые метрики позволяют более точно оценить рентабельность бизнеса 
          и рассчитать расширенные показатели, такие как EBITDA, рентабельность активов (ROA) и рентабельность собственного капитала (ROE). 
          Эти данные часто запрашивают инвесторы, банки и потенциальные партнеры.
        </p>
      </div>
    </div>
  );
};

export default AdditionalMetricsSection;